import {
  useEffect,
  useRef,
  useState,
  type ComponentProps,
  type SyntheticEvent,
} from "react";
import {
  Box,
  Button,
  Icon,
  TextInput,
  ParagraphText,
} from "@leagueplatform/genesis-core";
import { useIntl } from "@leagueplatform/locales";
import { driver } from "@leagueplatform/masonry";
import {
  MasonryFormFieldWrapper,
  MasonryTextInputRenderer,
  useInvokeAction,
} from "@leagueplatform/masonry-renderers";
import { type CodeKind, type CodeResult } from "./search-codes";
import { useCodeSearch } from "./use-code-search";

type Props = ComponentProps<typeof MasonryTextInputRenderer> & {
  selectedCode?: CodeResult;
};

export const CodeLookupField = ({
  kind,
  ...props
}: Props & { kind: CodeKind }) => {
  const {
    value = "",
    selectedCode,
    formFieldProperties,
    disabled,
    readOnly,
  } = props;
  const { formFieldId, label } = formFieldProperties;
  const { formatMessage } = useIntl();
  const invokeAction = useInvokeAction();
  const [query, setQuery] = useState(value);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [composing, setComposing] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const list = useRef<HTMLUListElement>(null);
  const listId = `${formFieldId}-results`;
  const search = useCodeSearch(
    kind,
    query,
    open && !composing && !disabled && !readOnly
  );
  const text = (
    id: `STR_${string}`,
    values?: Record<string, string | number>
  ) => formatMessage({ id }, values);
  const description =
    selectedCode?.code === value && query === value
      ? selectedCode.description
      : "";

  useEffect(() => {
    setQuery(value);
    setOpen(false);
    setActive(-1);
  }, [value]);
  useEffect(() => {
    if (active >= 0)
      list.current?.children[active]?.scrollIntoView({ block: "nearest" });
  }, [active]);

  const select = (item: CodeResult | null, event: SyntheticEvent) => {
    search.cancel();
    invokeAction({
      event,
      type: "setNodeState",
      namespace: "default",
      payload: {
        state: {
          value: item?.code || "",
          selectedCode: item,
          formFieldProperties: {
            ...formFieldProperties,
            inputStatus: undefined,
            statusMessage: undefined,
          },
        },
      },
    });
    setQuery(item?.code || "");
    setOpen(false);
    setActive(-1);
    input.current?.focus();
  };

  const getStatusMessage = () => {
    if (Array.from(query.trim()).length < 3)
      return text("STR_CODE_LOOKUP_MINIMUM");
    if (search.loading) return text("STR_CODE_LOOKUP_LOADING");
    if (search.failed) return text("STR_CODE_LOOKUP_ERROR");
    if (!search.items.length) return text("STR_CODE_LOOKUP_EMPTY");
    return null;
  };

  const statusMessage = getStatusMessage();

  return (
    <Box
      css={{ position: "relative", width: "100%" }}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node)) {
          search.cancel();
          setOpen(false);
          setQuery(value);
          setActive(-1);
        }
      }}
    >
      <MasonryFormFieldWrapper
        rootElementRef={props.rootElementRef}
        className={props.className}
        engineClassName={props.engineClassName}
        locatorId={props.locatorId}
        css={props.css}
        formFieldProperties={formFieldProperties}
      >
        <TextInput
          ref={input}
          id={formFieldId}
          role="combobox"
          value={query}
          autoComplete="off"
          placeholder={text("STR_CODE_LOOKUP_PLACEHOLDER")}
          disabled={disabled}
          readOnly={readOnly}
          leadingContent={<Icon icon="interfaceSearch" />}
          clearable={!!query && !disabled && !readOnly}
          onClear={(event) => select(null, event)}
          aria-label={label}
          aria-describedby={description ? `${formFieldId}-selected` : undefined}
          aria-invalid={
            formFieldProperties.inputStatus === "error" || undefined
          }
          aria-autocomplete="list"
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={open ? listId : undefined}
          aria-activedescendant={
            open && search.items[active] ? `${listId}-${active}` : undefined
          }
          onFocus={() => {
            if (!disabled && !readOnly) setOpen(true);
          }}
          onChange={(event) => {
            search.cancel();
            setQuery(event.currentTarget.value);
            setActive(-1);
            setOpen(true);
          }}
          onCompositionStart={() => {
            search.cancel();
            setComposing(true);
            setActive(-1);
          }}
          onCompositionEnd={() => setComposing(false)}
          onKeyDown={(event) => {
            if (
              composing ||
              event.nativeEvent.isComposing ||
              disabled ||
              readOnly
            )
              return;
            if (event.key === "Escape") {
              event.preventDefault();
              search.cancel();
              setOpen(false);
              setQuery(value);
              setActive(-1);
              return;
            }
            if (event.key === "ArrowDown" || event.key === "ArrowUp") {
              event.preventDefault();
              setOpen(true);
              setActive((index) =>
                event.key === "ArrowDown"
                  ? Math.min(index + 1, search.items.length - 1)
                  : Math.max(index - 1, 0)
              );
              return;
            }
            if (event.key === "Enter" && open) {
              event.preventDefault();
              if (search.items[active]) select(search.items[active], event);
            }
          }}
        />
      </MasonryFormFieldWrapper>
      {open && (
        <Box
          css={{
            position: "absolute",
            width: "100%",
            zIndex: "$dropdown",
            paddingBlock: "$threeQuarters",
            paddingInline: "$oneAndHalf",
            backgroundColor: "$surfaceBackgroundPrimary",
            borderRadius: "$extraLarge",
            boxShadow: "$dropdown",
          }}
        >
          {statusMessage && (
            <ParagraphText role="status" aria-live="polite" size="sm">
              {statusMessage}
            </ParagraphText>
          )}
          <Box
            as="ul"
            ref={list}
            id={listId}
            role="listbox"
            aria-label={label}
            aria-describedby={
              description ? `${formFieldId}-selected` : undefined
            }
            aria-invalid={
              formFieldProperties.inputStatus === "error" || undefined
            }
            aria-busy={search.loading}
            css={{
              listStyle: "none",
              margin: 0,
              padding: 0,
              maxHeight: "min(24rem, 45vh)",
              overflowY: "auto",
              overscrollBehavior: "contain",
            }}
          >
            {search.items.map((item, index) => (
              <Box
                as="li"
                key={item.code}
                id={`${listId}-${index}`}
                role="option"
                aria-selected={value === item.code}
                onMouseDown={(event) => event.preventDefault()}
                onClick={(event) => select(item, event)}
                onMouseMove={() => setActive(index)}
                css={{
                  padding: "$one",
                  cursor: "pointer",
                  overflowWrap: "anywhere",
                  borderRadius: "$small",
                  backgroundColor:
                    index === active
                      ? "$inputBackgroundHovered"
                      : "transparent",
                  outline: index === active ? "2px solid currentColor" : "none",
                  outlineOffset: -2,
                  "@media (forced-colors: active)": {
                    outlineColor: "Highlight",
                  },
                }}
              >
                <ParagraphText css={{ margin: 0 }}>
                  <strong>{item.code}</strong>
                  <br />
                  {item.description}
                </ParagraphText>
              </Box>
            ))}
          </Box>
          {search.failed && (
            <Button type="button" size="small" onClick={search.retry}>
              {text("STR_CODE_LOOKUP_RETRY")}
            </Button>
          )}
        </Box>
      )}
      {description && (
        <ParagraphText
          id={`${formFieldId}-selected`}
          size="sm"
          css={{ marginBlockStart: "$half", overflowWrap: "anywhere" }}
        >
          {description}
        </ParagraphText>
      )}
    </Box>
  );
};

export const CodeLookupRenderer = (props: Props) => {
  const { formFieldProperties } = props;
  const { formId, formFieldId } = formFieldProperties;
  if (
    (formId === "preAuthorizationCheckTool$form" ||
      formId === "preAuthorizationForm$form") &&
    ["procedureCode", "procedure", "diagnosisCode"].includes(formFieldId)
  ) {
    return (
      <CodeLookupField
        {...props}
        kind={formFieldId === "diagnosisCode" ? "diagnosis" : "procedure"}
      />
    );
  }
  return <MasonryTextInputRenderer {...props} />;
};

let registered = false;
export const registerCodeLookupRenderer = () => {
  if (registered) return;
  driver.nodeRendererRegistry.overrideRenderer(
    "textInput",
    "emblem-code-lookup",
    CodeLookupRenderer
  );
  registered = true;
};
